import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { authAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle, Eye, EyeOff, ShieldCheck } from 'lucide-react';

const OIDC_MESSAGES = {
  pending_approval: 'Tu acceso con Microsoft esta pendiente de aprobacion del administrador.',
  domain_denied: 'Tu dominio de email no esta autorizado en este tenant.',
  tenant_denied: 'Tu cuenta de Microsoft no pertenece a la organizacion habilitada.',
  denied: 'El acceso con Microsoft esta denegado.',
  admin_use_password: 'Las cuentas admin deben ingresar con email y contrasena local.',
  token_failed: 'No se pudo validar el login de Microsoft. Reintenta.',
  invalid_client_secret:
    'Secret de la app Microsoft invalido en el servidor. En Azure usa el Valor del secreto (no el Id secreto) en OMNISYS_ENTRA_CLIENT_SECRET y reinicia el backend.',
  invalid_client: 'Credenciales de la app Microsoft invalidas en el servidor. Revisa Client ID/Secret.',
  redirect_mismatch: 'El redirect URI no coincide con el registrado en Azure.',
  invalid_scope: 'Scopes OIDC invalidos para la app Microsoft.',
  azure_denied: 'Microsoft rechazo el inicio de sesion.',
  invalid_state: 'Sesion OIDC invalida o expirada. Reintenta.',
  missing_code: 'Respuesta incompleta de Microsoft.',
  no_email: 'Microsoft no devolvio un email utilizable.',
  provision_failed: 'No se pudo crear o vincular el usuario.',
};

/**
 * El segundo paso del login con contraseña (2026-10-13): el código de Google Authenticator.
 *
 * Toda cuenta local lo necesita. Si todavía no lo configuró (`proposito: configurar`), primero se
 * muestra el QR para darlo de alta, y se entra al confirmar el primer código. El permiso temporal
 * dura cinco minutos: si vence, se vuelve a la contraseña.
 */
function SegundoPaso({ paso, onListo, onVolver }) {
  const [codigo, setCodigo] = useState('');
  const [alta, setAlta] = useState(null);
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);
  const configurar = paso.proposito === 'configurar';

  useEffect(() => {
    if (!configurar) return;
    authAPI.configurarSegundoFactor(paso.desafio)
      .then((r) => setAlta(r.data))
      .catch((e) => setError(e?.response?.data?.detail || 'No se pudo preparar el autenticador'));
  }, [configurar, paso.desafio]);

  const enviar = async (e) => {
    e.preventDefault();
    setError('');
    setEnviando(true);
    try {
      const fn = configurar ? authAPI.activarSegundoFactor : authAPI.verificarSegundoFactor;
      const res = await fn(paso.desafio, codigo);
      onListo(res.data);
    } catch (err) {
      const detalle = err?.response?.data?.detail || 'El código no es válido';
      if (String(detalle).includes('venció')) { onVolver(detalle); return; }
      setError(detalle);
      setCodigo('');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <form onSubmit={enviar} className="space-y-4" data-testid="login-segundo-factor">
      <div className="flex items-start gap-2 text-sm text-zinc-700">
        <ShieldCheck className="w-4 h-4 mt-0.5 text-brand-primary shrink-0" />
        {configurar ? (
          <p>
            Tu cuenta necesita un segundo factor. Escaneá este código con <b>Google Authenticator</b>
            {' '}(o cualquier app de códigos) y escribí los 6 dígitos que te muestre.
          </p>
        ) : (
          <p>Escribí el código de 6 dígitos que muestra tu app de autenticación.</p>
        )}
      </div>
      {configurar && alta && (
        <div className="flex flex-col items-center gap-2" data-testid="login-qr">
          <img src={alta.qr} alt="Código QR para el autenticador" className="w-48 h-48 border border-brand-border" />
          <p className="text-[11px] text-zinc-500 text-center">
            ¿No podés escanear? Cargá esta clave a mano:
            <span className="block font-mono text-zinc-700 break-all mt-0.5">{alta.secreto}</span>
          </p>
        </div>
      )}
      <Input
        data-testid="login-codigo-input"
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus
        maxLength={6}
        value={codigo}
        onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ''))}
        placeholder="123456"
        className="h-10 border-brand-border text-center tracking-[0.5em] font-mono text-lg rounded-sm"
        required
      />
      {error && (
        <div className="flex items-center gap-2 text-brand-danger text-sm" data-testid="login-error">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}
      <Button
        type="submit"
        data-testid="login-codigo-submit"
        disabled={enviando || codigo.length !== 6 || (configurar && !alta)}
        className="w-full h-10 bg-brand-primary hover:bg-[#001F6D] text-white font-semibold text-sm rounded-sm"
      >
        {enviando ? 'Verificando...' : (configurar ? 'Activar y entrar' : 'Entrar')}
      </Button>
      <button type="button" onClick={() => onVolver('')} className="w-full text-xs text-zinc-500 hover:underline">
        Volver a la contraseña
      </button>
    </form>
  );
}

export default function LoginPage() {
  const { login, completarLogin, setSessionExpiredOpen } = useAuth();
  const [paso, setPaso] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [msEnabled, setMsEnabled] = useState(false);
  const tenantHint = 'main';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const oidcErr = params.get('oidc_error');
        if (oidcErr) {
          setError(OIDC_MESSAGES[oidcErr] || decodeURIComponent(oidcErr));
        }
        const res = await authAPI.oidcMicrosoftStatus(tenantHint);
        if (!cancelled) setMsEnabled(!!res.data?.microsoft_enabled);
      } catch {
        if (!cancelled) setMsEnabled(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const datos = await login(email, password);
      if (datos?.segundo_factor) {
        setPaso({ proposito: datos.segundo_factor, desafio: datos.desafio });
        return;
      }
      setSessionExpiredOpen(false);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Error de autenticacion');
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoft = () => {
    window.location.href = authAPI.oidcMicrosoftStartUrl(tenantHint);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 bg-[#09090B] items-center justify-center p-12">
        <div className="max-w-md">
          <h1 className="font-heading text-5xl font-bold text-white tracking-tighter leading-none mb-4">
            OMNISYS
          </h1>
          <p className="text-zinc-400 font-body text-lg leading-relaxed">
            Framework multiagente omnicanal para soporte IT y gestion de proyectos.
          </p>
          <div className="mt-8 border-t border-zinc-800 pt-6">
            <div className="flex items-center gap-3 text-zinc-500 text-xs font-mono uppercase tracking-widest">
              <span className="w-2 h-2 bg-brand-success rounded-full" />
              Sistema operativo
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <h2 className="font-heading text-2xl font-bold text-[#09090B] tracking-tight">
              Iniciar sesion
            </h2>
            <p className="text-sm text-zinc-500 font-body mt-1">
              Ingresa tus credenciales para acceder a la consola
            </p>
          </div>

          {msEnabled && !paso && (
            <div className="mb-4 space-y-3">
              <Button
                type="button"
                variant="outline"
                data-testid="login-microsoft-button"
                onClick={handleMicrosoft}
                className="w-full h-10 border-brand-border text-sm font-semibold rounded-sm"
              >
                Entrar con Microsoft
              </Button>
              <div className="relative py-1">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-zinc-200" />
                </div>
                <div className="relative flex justify-center text-[10px] uppercase tracking-widest">
                  <span className="bg-white px-2 text-zinc-400">o con cuenta local</span>
                </div>
              </div>
            </div>
          )}

          {paso ? (
            <SegundoPaso
              paso={paso}
              onListo={(datos) => { completarLogin(datos); setSessionExpiredOpen(false); }}
              onVolver={(motivo) => { setPaso(null); setPassword(''); setError(motivo); }}
            />
          ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-700">
                Email
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="usuario@empresa.com"
                className="h-10 border-brand-border focus:ring-2 focus:ring-brand-primary text-sm font-body rounded-sm"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-700">
                Contrasena
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-10 border-brand-border focus:ring-2 focus:ring-brand-primary text-sm font-body rounded-sm pr-10"
                  required
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-brand-danger text-sm" data-testid="login-error">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full h-10 bg-brand-primary hover:bg-[#001F6D] text-white font-semibold text-sm rounded-sm transition-colors duration-150"
            >
              {loading ? 'Ingresando...' : 'Ingresar'}
            </Button>
          </form>
          )}
        </div>
      </div>
    </div>
  );
}
